import React from 'react'
import { Box, IconButton, Stack, Theme, Tooltip, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useMediaQuery } from '@mui/system'
import { E3FavoriteSelected, J2Documentcopy, J2Download, K1View } from '@e3dc-react/icons'
import { FileFavoritesModel } from '../../models'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { format } from '@e3dc-react/shell/Libs/date'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { removeFileFavorite } from '../../api'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { useDispatch } from 'react-redux'
import DownloadItemBreadcrumbs from '../search-file/DownloadItemBreadcrumbs'
import { useDownloadItemActions } from '../../hooks'
import { useIsWebviewMode } from 'src/Hooks'
import { isAndroid } from '@e3dc-react/shell/Configuration'
import { ALLOWED_FILE_TYPES } from '../../consts'

interface FileNodeProps {
  file: FileFavoritesModel
}

const useStyles = makeStyles()(theme => ({
  wrapper: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: theme.palette.shades.shade03,
    maxWidth: '100%',
    overflow: 'hidden',
  },
}))

const FileNode: React.FC<FileNodeProps> = ({ file }) => {
  const { classes } = useStyles()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const dispatch = useDispatch()
  const downSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const { t } = useCustomTranslation()
  const createdAt = format(new Date(file.createdAt), t('common.format.date-fns-date'))
  const { viewFile, downloadFile } = useDownloadItemActions()
  const isWebview = useIsWebviewMode()
  const hidePreviewButton = isWebview && isAndroid
  const isViewButtonEnabled = ALLOWED_FILE_TYPES.includes(file.fileType?.toLowerCase() ?? '')

  const removeFavorite = async (): Promise<void> => {
    try {
      const fileResult = await removeFileFavorite(file.downloadItemId, authFetch)
      if (!fileResult.hasErrors) {
        dispatch(refreshApiView())
        enqueueSuccessStack(t('downloads.file-favorite-remove.success'))
      } else enqueueErrorStack(t('downloads.file-favorite-remove.error'))
    } catch (error) {
      console.error('Error setting file favorite', error)
      enqueueErrorStack(t('downloads.file-favorite-remove.error'))
    }
  }

  return (
    <Stack className={classes.wrapper} flexGrow={1} direction={'column'} spacing={2} alignItems="flex-start">
      <Stack width={'100%'} direction={'row'} gap={2} alignItems="center">
        {!downSm && <J2Documentcopy />}
        <Stack id={'text-group'} flexGrow={1} gap={1} sx={{ overflow: 'hidden' }}>
          <DownloadItemBreadcrumbs iconStyle={{ fontSize: 18 }} parentCategories={file.parents} onClickCategory={() => undefined} />
          <Stack direction={'row'} gap={2}>
            {downSm && <J2Documentcopy />}
            <Typography variant="h3" noWrap>
              {file.fileName}
            </Typography>
          </Stack>
          <Stack direction={'row'} gap={2}>
            <Typography variant="subtitle2">{file.fileType?.toLocaleUpperCase()}</Typography>
            <Typography sx={{ lineHeight: 0.5 }}>.</Typography>
            <Typography variant="subtitle2">{createdAt}</Typography>
          </Stack>
        </Stack>
      </Stack>
      <Stack width={'100%'} direction={'row'} justifyContent={'space-between'} alignItems="center">
        <IconButton sx={{ padding: 0 }} onClick={removeFavorite}>
          <E3FavoriteSelected />
        </IconButton>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          {!hidePreviewButton && (
            <Tooltip title={t('files.download-item.view-file-tooltip')} disableHoverListener={isViewButtonEnabled}>
              {/* Note this Box is needed for disabled buttons to show a tooltip on safari see https://mui.com/material-ui/react-tooltip/#disabled-elements */}
              <Box>
                <IconButton disabled={!isViewButtonEnabled} onClick={() => viewFile(file.downloadItemId)}>
                  <K1View />
                </IconButton>
              </Box>
            </Tooltip>
          )}
          <IconButton onClick={() => downloadFile(file.downloadItemId)}>
            <J2Download />
          </IconButton>
        </Box>
      </Stack>
    </Stack>
  )
}

export default FileNode
